import { useState } from "react";
import { devNotesAssetDelete, devNotesUpdate } from "@/lib/devActionableNotesClient";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { noteAssetIncludeInSync, noteHasAsset } from "./devActionableNoteTypes";
import { DevPanelActionableNoteMeta } from "./DevPanelActionableNoteCard";

type Props = {
  note: DevActionableNote;
  getToken: () => Promise<string | null>;
  onError: (msg: string) => void;
  onDone: () => void;
  busy?: boolean;
  canManageAsset?: boolean;
};

export function DevPanelActionableNoteMetaWithAssetControls({
  note,
  getToken,
  onError,
  onDone,
  busy = false,
  canManageAsset = false,
}: Props) {
  const [includeInSync, setIncludeInSync] = useState(noteAssetIncludeInSync(note));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!noteHasAsset(note)) {
    return <DevPanelActionableNoteMeta note={note} />;
  }

  async function toggleInclude(next: boolean) {
    setIncludeInSync(next);
    const token = await getToken();
    if (!token) return;
    setSaving(true);
    const result = await devNotesUpdate(token, { id: note.id, include_asset_in_sync: next });
    setSaving(false);
    if (!result.ok) {
      onError(result.error);
      setIncludeInSync(!next);
      return;
    }
    onDone();
  }

  async function deleteAsset() {
    const token = await getToken();
    if (!token) return;
    setDeleting(true);
    const result = await devNotesAssetDelete(token, { id: note.id });
    setDeleting(false);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    onDone();
  }

  return (
    <DevPanelActionableNoteMeta
      note={note}
      assetInteractive={
        canManageAsset
          ? {
              interactive: true,
              includeInSync,
              onIncludeInSyncChange: (next) => void toggleInclude(next),
              onDelete: () => void deleteAsset(),
              deleting,
              disabled: busy || saving || deleting,
            }
          : undefined
      }
    />
  );
}
